(() => {
  const SEL = 'product-price[data-mw-discount-enabled="true"]';
  const INIT = 'data-mw-discount-init';

  const cssEscape = (s) => {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/["\\]/g, '\\$&');
  };

  function getRoot(priceEl) {
    return (
      priceEl.closest('product-info') ||
      priceEl.closest('.product') ||
      priceEl.closest('.shopify-section') ||
      document
    );
  }

  function readJson(priceEl) {
    const el = priceEl.querySelector('script[data-mw-discount-json]');
    if (!el) return null;
    try { return JSON.parse(el.textContent || '{}'); }
    catch (_) { return null; }
  }

  function buildByOpts(data) {
    const out = new Map();
    const byId = (data && data.byId) || {};
    for (const id in byId) {
      const v = byId[id];
      const key = Array.isArray(v.opts) ? v.opts.join('||') : '';
      if (key) out.set(key, v);
    }
    return out;
  }

  function readVariantId(priceEl) {
    const root = getRoot(priceEl);

    // Prefer the closest add-to-cart form in this product area
    const forms = root.querySelectorAll('form[action*="/cart/add"]');
    if (forms && forms.length) {
      for (const f of forms) {
        const input = f.querySelector('input[name="id"]');
        if (input && input.value) return String(input.value);
      }
    }

    // Fallback: any id input in the same root
    const input = root.querySelector('input[name="id"]');
    return input && input.value ? String(input.value) : '';
  }

  function readSelectedOptions(priceEl, data) {
    const root = getRoot(priceEl);
    const meta = (data && data.meta) || {};
    const optionNames = Array.isArray(meta.optionNames) ? meta.optionNames : [];
    const optionCount = Number(meta.optionCount || optionNames.length || 0);
    if (!optionCount) return null;

    // Most themes: data-option-position on selects/radios
    const picked = [];
    for (let pos = 1; pos <= optionCount; pos++) {
      let val = null;

      const sel = root.querySelector(`select[data-option-position="${pos}"]`);
      if (sel) val = sel.value;

      if (!val) {
        const r = root.querySelector(`input[type="radio"][data-option-position="${pos}"]:checked`);
        if (r) val = r.value;
      }

      if (!val) {
        const fs = root.querySelector(`fieldset[data-option-position="${pos}"] input[type="radio"]:checked`);
        if (fs) val = fs.value;
      }

      picked.push(val ? String(val) : '');
    }
    if (picked.some(v => v)) return picked;

    // Fallback: options[Name]
    const pickedByName = [];
    for (let i = 0; i < optionNames.length; i++) {
      const name = optionNames[i];
      const esc = cssEscape(name);

      const s1 = root.querySelector(`select[name="options[${esc}]"]`);
      if (s1) { pickedByName.push(String(s1.value || '')); continue; }

      const r1 = root.querySelector(`input[type="radio"][name="options[${esc}]"]:checked`);
      if (r1) { pickedByName.push(String(r1.value || '')); continue; }

      pickedByName.push('');
    }
    if (pickedByName.some(v => v)) return pickedByName;

    return null;
  }

  function resolveVariant(priceEl, state, forcedId) {
    const data = state.data;
    const byId = data.byId || {};

    // 0) If event provides an id, use it
    if (forcedId && byId[forcedId]) return byId[forcedId];

    // 1) Options matching (best)
    const opts = readSelectedOptions(priceEl, data);
    if (opts) {
      const key = opts.join('||');
      const v = state.byOpts.get(key);
      if (v) return v;
    }

    // 2) Hidden variant id (good fallback)
    const vid = readVariantId(priceEl);
    if (vid && byId[vid]) return byId[vid];

    // 3) Initial
    if (state.initialId && byId[state.initialId]) return byId[state.initialId];

    return null;
  }

  function initOne(priceEl) {
    if (!priceEl || priceEl.getAttribute(INIT) === '1') return;

    // CRITICAL: Only update blocks that include a variants JSON map.
    // Catalog cards should NOT include this JSON, so they will never be touched.
    const data = readJson(priceEl);
    if (!data || !data.byId) return;

    const enabled = priceEl.getAttribute('data-mw-discount-enabled') === 'true';
    const percent = Number(priceEl.getAttribute('data-mw-discount-percent') || 0);
    if (!enabled || !(percent > 0)) return;

    const nativeWrap = priceEl.querySelector('[data-mw-native-price]');
    const discountWrap = priceEl.querySelector('[data-mw-discount-price]');
    const originalEl = discountWrap && discountWrap.querySelector('[data-mw-original]');
    const newEl = discountWrap && discountWrap.querySelector('[data-mw-new]');
    if (!nativeWrap || !discountWrap || !originalEl || !newEl) return;

    const state = {
      priceEl,
      data,
      byOpts: buildByOpts(data),
      nativeWrap,
      discountWrap,
      originalEl,
      newEl,
      initialId: String(priceEl.getAttribute('data-mw-initial-variant-id') || ''),
      raf: 0,
      tick: 0,
      forcedId: ''
    };

    function apply(v) {
      if (!v) return;
      state.originalEl.textContent = v.orig || '';
      state.newEl.textContent = v.disc || '';
      state.nativeWrap.style.display = 'none';
      state.discountWrap.style.display = 'inline-flex';
    }

    function updateNow() {
      const v = resolveVariant(priceEl, state, state.forcedId);
      state.forcedId = '';
      apply(v);
    }

    function schedule() {
      state.tick++;
      const myTick = state.tick;

      if (state.raf) cancelAnimationFrame(state.raf);
      state.raf = requestAnimationFrame(() => {
        state.raf = 0;
        if (myTick !== state.tick) return;
        updateNow();
      });
    }

    priceEl.setAttribute(INIT, '1');

    const root = getRoot(priceEl);

    // Any user change in the product area
    root.addEventListener('change', schedule, true);
    root.addEventListener('input', schedule, true);

    // Many themes dispatch variant events with detail.variant.id
    function onVariantEvent(e) {
      const id =
        e && e.detail && e.detail.variant && e.detail.variant.id
          ? String(e.detail.variant.id)
          : '';
      if (id) state.forcedId = id;
      schedule();
    }
    document.addEventListener('variant:change', onVariantEvent);
    document.addEventListener('product:variant-change', onVariantEvent);

    // Theme re-renders markup? Fine.
    const mo = new MutationObserver(schedule);
    mo.observe(root, { subtree: true, childList: true, attributes: true });

    schedule();
  }

  function initAll(root = document) {
    root.querySelectorAll(SEL).forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Load more / injected DOM
  const pageMo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches && node.matches(SEL)) initOne(node);
        if (node.querySelectorAll) initAll(node);
      }
    }
  });
  pageMo.observe(document.documentElement, { childList: true, subtree: true });
})();
